import { Controller, useForm } from 'react-hook-form';
import { useAuthModalStore } from '../../../store/auth-modal';

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';
import { ErrorMessage } from '../auth-modal/ErrorMessage';
import { useAxios } from '../../../hooks/useAxios/useAxios';
import Select from 'react-select';
import { UsaStates } from 'usa-states';
import { toast } from 'react-toastify';
import { ActionsAdr } from '../../../utils/data/account';
import { AddressCardSm } from './addressCardSm';
import { useAdrValidation } from './adrModalHook';
import { PhoneInputWithRef } from './PhoneInputWithRef.';
import {
  emailMaxLength,
  inputMaxLength,
  zipMaxLength
} from '../../../utils/validation/validation-rules';
import { yupResolver } from '@hookform/resolvers/yup';
import { addressSchema } from '../../../utils/validation/address-schema';
import cn from 'classnames';
import DefaultCheckbox from '../default-checkbox';

export const AddressForm = forwardRef((props, ref) => {
  const [ValidModal, modalData, closeModal, validateAdr] = useAdrValidation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 575);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
    reset,
    trigger
  } = useForm({
    resolver: yupResolver(addressSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      company: '',
      country: 'Unites States',
      street_1: '',
      street_2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      isDefault: false
    }
  });

  const { formData, ...state } = useAuthModalStore();

  const adrFormValues = watch();

  useEffect(() => {
    if (props.isAddressConfirmed) {
      props.setAddressFromAddressForm(adrFormValues);
    }
  }, [props.isAddressConfirmed]);

  const statesSelectValues = useMemo(() => {
    const usStates = new UsaStates();
    return usStates.states.map(state => ({
      value: state.name,
      label: state.name
    }));
  }, []);
  const { execute: updateAddress } = useAxios(
    '/public/buyontrust/customers/address',
    {
      method: 'put'
    },
    false
  );

  const { execute: addAddress } = useAxios(
    '/public/buyontrust/customers/address',
    {
      method: 'post'
    },
    false
  );

  const onCancel = () => {
    !props.fromCheckout && reset();
    if (props.cancel) {
      props.cancel();
    }
  };

  const onSubmit = async data => {
    const adrObj = {
      address: {
        city: data.city,
        state: data.state.value,
        zip: data.zip,
        street1: data.street_1,
        street2: data.street_2
      }
    };
    const isAdrCorrect = await validateAdr(adrObj);

    if (isAdrCorrect) {
      chooseAdrHandle();
    }
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit(onSubmit)(event);
    }
  };

  const chooseAdrHandle = async (adrData = {}) => {
    const requestData = {
      token: state?.ddToken,
      ...adrFormValues,
      state: adrFormValues?.state?.value,
      ...adrData,
      phone: adrFormValues.phone?.replace(/\D/g, ''),
      zipcode: adrData.zip,
      address1: !!Object.keys(adrData).length
        ? adrData.street_1
        : adrFormValues.address1,
      address2: !!Object.keys(adrData).length
        ? adrData.street_2
        : adrFormValues.address2
    };
    if (props.fromCheckout) {
      requestData.isAccountAddress = false;
    }

    try {
      if (props.adrAction === ActionsAdr.edit) {
        const resp = await updateAddress(requestData);
        if (resp.success) {
          toast('Address updated successfully', {
            autoClose: 5000,
            style: { background: '#07bc0c', color: 'white' }
          });
          closeModal();
          setTimeout(() => {
            onCancel();
          }, 850);
        }
      } else {
        const resp = await addAddress(requestData);
        if (resp.success) {
          closeModal();
          setTimeout(() => {
            onCancel();
          }, 850);
        }
      }

      props.refresh ? props.refresh() : '';
    } catch (e) {
      toast.error(e.message, { theme: 'colored' });
    }
  };

  const triggerValidation = async () => {
    const isValid = await trigger();
    return isValid;
  };

  const requestAdrValidation = event => {
    handleSubmit(onSubmit)(event);
  };

  const requestSubmit = event => {
    chooseAdrHandle(event);
  };

  const updateMedia = () => {
    setIsMobile(window.innerWidth <= 575);
  };

  const customSelectStyles = {
    dropdownIndicator: provided => ({
      ...provided,
      paddingRight: '16px',
      paddingTop: '11px'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    })
  };

  useImperativeHandle(ref, () => ({
    triggerValidation,
    requestAdrValidation,
    requestSubmit,
    getFormData: () => adrFormValues
  }));

  useEffect(() => {
    window.addEventListener('resize', updateMedia);
    return () => window.removeEventListener('resize', updateMedia);
  }, []);

  useEffect(() => {
    if (props.onFormChange) {
      if (isDirty) {
        props.onFormChange(true);
      } else {
        props.onFormChange(false);
      }
    }
  }, [isDirty]);

  useEffect(() => {
    if (props.editAdrData) {
      const data = {
        ...props.editAdrData,
        state: {
          value: props.editAdrData?.state,
          label: props.editAdrData?.state
        }
      };
      reset(data);
    }
  }, [props.editAdrData, reset]);

  return (
    <>
      <form
        className="w-lg-max"
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={e => !props.disableKeyDown && handleKeyDown(e)}
        ref={ref}>
        {state.message && (
          <ErrorMessage bg={state.statusBg} message={state.message} />
        )}
        {!props.hideTitle && (
          <div className="row p-3 text-uppercase">
            <h4>
              {props.adrAction === ActionsAdr.add
                ? 'Add new address'
                : `Edit address`}
            </h4>
          </div>
        )}

        <div className="row">
          {/* First Name */}
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="acc-name">
                First Name <span className="required"> *</span>
              </label>
              <input
                {...register('first_name')}
                className={cn('form-control', {
                  'form-control-error': errors.first_name
                })}
                id="acc-name"
                type="text"
                maxLength={inputMaxLength}
                autocomplete="given-name"
                // placeholder="Editor"
                disabled={props.isDisabled || false}
              />
              {errors.first_name && (
                <p className={'text-danger'}>{errors.first_name.message}</p>
              )}
            </div>
          </div>
          {/* Last Name */}
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="acc-lastname">
                Last Name <span className="required"> *</span>
              </label>
              <input
                {...register('last_name')}
                className={cn('form-control', {
                  'form-control-error': errors.last_name
                })}
                id="acc-lastname"
                type="text"
                maxLength={inputMaxLength}
                autocomplete="family-name"
                disabled={props.isDisabled || false}
              />
              {errors.last_name && (
                <p className={'text-danger'}>{errors.last_name.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Company Name */}
          <div className="col-md-12">
            <div className="form-group">
              <label htmlFor="acc-company">Company Name (Optional)</label>
              <input
                {...register('company')}
                className={cn('form-control', {
                  'form-control-error': errors.company
                })}
                id="acc-company"
                type="text"
                maxLength={inputMaxLength}
                autocomplete="organization"
                disabled={props.isDisabled || false}
              />
              {errors.company && (
                <p className={'text-danger'}>{errors.company.message}</p>
              )}
            </div>
            {/* {props.adrAction === ActionsAdr.add
              ? 'Add new address'
              : 'Edit address'} */}
          </div>
        </div>

        <div className="row">
          {/* Address Line 1 */}
          <div className="col-md-12">
            <div className="form-group">
              <label htmlFor="acc-address1">
                Address Line 1 <span className="required"> *</span>
              </label>
              <input
                {...register('street_1')}
                className={cn('form-control', {
                  'form-control-error': errors.street_1
                })}
                id="acc-address1"
                type="text"
                autocomplete="address-line1"
                maxLength={inputMaxLength}
                disabled={props.isDisabled || false}
              />
              {errors.street_1 && (
                <p className={'text-danger'}>{errors.street_1.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Address Line 2 */}
          <div className="col-md-12">
            <div className="form-group">
              <label htmlFor="acc-address2">Address Line 2</label>
              <input
                {...register('street_2')}
                className={cn('form-control', {
                  'form-control-error': errors.street_2
                })}
                id="acc-address2"
                type="text"
                maxLength={inputMaxLength}
                autocomplete="address-line2"
                disabled={props.isDisabled || false}
              />
              {errors.street_2 && (
                <p className={'text-danger'}>{errors.street_2.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Suburb/City */}
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="acc-city">
                City <span className="required"> *</span>
              </label>
              <input
                {...register('city')}
                className={cn('form-control', {
                  'form-control-error': errors.city
                })}
                id="acc-city"
                type="text"
                maxLength={inputMaxLength}
                autocomplete="address-level2"
                disabled={props.isDisabled || false}
              />
              {errors.city && (
                <p className={'text-danger'}>{errors.city.message}</p>
              )}
            </div>
          </div>
          {/* State/Province */}
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="acc-state">
                State <span className="required"> *</span>
              </label>
              <Controller
                control={control}
                name="state"
                render={({ field }) => (
                  <Select
                    styles={customSelectStyles}
                    className={cn(
                      [
                        'form-control-select',
                        props.isDisabled ? 'disabledSelect' : ''
                      ],
                      {
                        'form-control-error': errors.state
                      }
                    )}
                    {...field}
                    inputId="acc-state"
                    options={statesSelectValues}
                    isDisabled={props.isDisabled || false}
                  />
                )}
              />
              {errors.state && (
                <p className={'text-danger'}>{errors.state.message}</p>
              )}
            </div>
          </div>
          {/* Zip/Postcode */}
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="acc-zipcode">
                Zip Code<span className="required"> *</span>
              </label>
              <input
                {...register('zip')}
                className={cn('form-control', {
                  'form-control-error': errors.zip
                })}
                id="acc-zipcode"
                type="string"
                maxLength={zipMaxLength}
                autocomplete="postal-code"
                disabled={props.isDisabled || false}
              />
              {errors.zip && (
                <p className={'text-danger'}>{errors.zip.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Phone */}
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="acc-name">
                Phone <span className="required"> *</span>
              </label>
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <PhoneInputWithRef
                    {...field}
                    id="acc-phone"
                    country={'us'}
                    onlyCountries={['us', 'ua']}
                    disableDropdown={true}
                    countryCodeEditable={false}
                    inputClass={cn('form-control phone-input', {
                      'form-control-error': errors.phone
                    })}
                    placeholder={'PHONE #'}
                    disabled={
                      props.isDisabled ||
                      (props.adrAction === ActionsAdr.edit && props.islast)
                    }
                  />
                )}
              />
              {errors.phone && (
                <p className={'text-danger'}>{errors.phone.message}</p>
              )}
            </div>
          </div>
          {/* Email Address */}
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="acc-email">
                Email Address <span className="required"> *</span>
              </label>
              <input
                {...register('email')}
                className={cn('form-control', {
                  'form-control-error': errors.email
                })}
                type="email"
                id="acc-email"
                maxLength={emailMaxLength}
                autocomplete="email"
                disabled={props.isDisabled || false}
                // placeholder="editor@gmail.com"
              />
              {errors.email && (
                <p className={'text-danger'}>{errors.email.message}</p>
              )}
            </div>
          </div>
        </div>
        {/* default checkbox */}
        {!props?.hideTitle && (
          <DefaultCheckbox
            isDisabled={props.editAdrData?.isDefault}
            register={register('isDefault')}
            text="Set to default"
          />
          //   <div className="col-md-6 d-flex">
          //     <div className="form-group">
          //       <input
          //         {...register('isDefault')}
          //         className="adr-checkbox"
          //         type="checkbox"
          //         id="acc-company"
          //       />
          //       <span>Set to default</span>
          //     </div>
          //   </div>
        )}

        {/* Submit Button */}
        {!props?.hideTitle && (
          <div className="mt-3 d-flex justify-content-center">
            <button
              className="btn w-25 btn-light mr-5 btn-md address-form-btn"
              onClick={onCancel}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn w-25 btn-dark mr-0 btn-md address-form-btn">
              Save Address
            </button>
          </div>
        )}
      </form>

      {/* Address Validation Modal */}
      <ValidModal>
        {isMobile ? (
          <div className="adr-modal-mobile">
            <h1 className="adr-modal-mobile__title">
              The address you&apos;ve entered can not match postal service
              records
            </h1>
            <div className="adr-modal-mobile__block">
              <AddressCardSm addressData={adrFormValues} />
              <button
                className={'btn btn-light btn-md adr-modal-mobile__btn'}
                onClick={() => chooseAdrHandle()}>
                USE THIS ADDRESS
              </button>
            </div>

            <span className="adr-modal-mobile__or">OR</span>

            {modalData?.isValid && (
              <div className="adr-modal-mobile__block">
                <AddressCardSm
                  addressData={{
                    ...modalData.addressValidated,
                    first_name: adrFormValues.first_name,
                    last_name: adrFormValues.last_name
                  }}
                />
                <button
                  className={'btn btn-dark btn-md adr-modal-mobile__btn'}
                  onClick={() => chooseAdrHandle(modalData.addressValidated)}>
                  USE THIS ADDRESS
                </button>
              </div>
            )}
            {!modalData?.isValid && (
              <div className="adr-modal-mobile__block">
                <span className={'text-center text-dark mb-1'}>
                  We can not automatically confirm this address. Please check it
                  carefully.
                </span>
                <button
                  className={'btn btn-dark btn-md adr-modal-mobile__btn'}
                  onClick={closeModal}>
                  BACK
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className={`row d-flex flex-column align-content-center`}>
            <span className={'text-center text-dark font-weight-bold m-2'}>
              The address you&apos;ve entered can not match postal service
              records
            </span>
            <div className="col-md-10 d-flex ml-5">
              <AddressCardSm addressData={adrFormValues} className="ml-5" />
              {modalData?.isValid && (
                <AddressCardSm
                  addressData={{
                    ...modalData.addressValidated,
                    first_name: adrFormValues.first_name,
                    last_name: adrFormValues.last_name
                  }}
                />
              )}
              {!modalData?.isValid && (
                <div
                  className={
                    'address-card w-100 d-flex flex-column align-items-center mt-3'
                  }>
                  <span className={'text-center text-dark mb-2'}>
                    We can not automatically confirm this address. Please check
                    it carefully.
                  </span>
                  {/* <div className={'card-divider align-self-center mt-1'}></div> */}
                </div>
              )}
            </div>
            <div
              className={
                'mt-2 d-flex justify-content-center align-items-center'
              }>
              <button
                className="use-address-btn mr-5"
                onClick={() => chooseAdrHandle()}>
                USE THIS ADDRESS
              </button>
              <span
                className={
                  'text-center text-uppercase text-dark font-weight-bold'
                }>
                or
              </span>
              {modalData?.addressValidated ? (
                <button
                  className="use-address-btn btn-dark ml-5"
                  onClick={() => chooseAdrHandle(modalData.addressValidated)}>
                  USE THIS ADDRESS
                </button>
              ) : (
                <button
                  className={'btn w-25 btn-dark ml-5 btn-md'}
                  onClick={closeModal}>
                  BACK
                </button>
              )}
            </div>
          </div>
        )}
      </ValidModal>
    </>
  );
});
